import { ChangeEvent, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import TableBody from '@material-ui/core/TableBody';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TablePagination, TableCell } from '@/shared/components/MaterialTableElements';
import { LoadingSpinner } from '@/shared/components/Loading';
import { MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import LiveMeditationSession, { MEDITATION_TYPE_LABELS, deriveLiveSessionStatus } from '@/models/live-meditation-session';
import { useLiveSessions, useRemoveLiveSession } from '@/services/live-sessions';
import CreateSessionModal from './components/create-session-modal';

const STATUS_COLOURS: Record<string, string> = {
    live: '#28a745',
    scheduled: '#ffc107',
    draft: '#6c757d',
    ended: '#6c757d',
    cancelled: '#dc3545',
};

const formatWhen = (session: LiveMeditationSession): string => {
    const at = session.startedAt || session.scheduledAt;
    if (!at) return 'Not scheduled';
    return new Date(at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** A compact statement of what the host allows, so the table says it without a click. */
const describePolicies = (session: LiveMeditationSession): string => {
    const label = (policy: string) => (policy === 'free' ? 'open' : policy === 'on-request' ? 'on request' : 'off');
    return `mic ${label(session.settings?.audioPolicy)} · cam ${label(session.settings?.videoPolicy)}`;
};

/**
 * Live sessions — staff create the coach-led ones here and can pull any of them.
 *
 * Two things this screen gets right on purpose:
 *
 * - **Status is DERIVED** from the timings on every render, never read from the
 *   stored `status`. That field is a lagging cache a sweep tidies up, so a table
 *   trusting it would show sessions as live for minutes after they ended.
 * - **It lists MEMBER-created sessions too.** A session title is user-authored
 *   free text that appears in a browse list, so without this the only way to pull
 *   an abusive one would be editing Firestore by hand.
 */
const LiveSessions = () => {
    const { data, isLoading } = useLiveSessions();
    const { mutateAsync: removeSession, isPending: removing } = useRemoveLiveSession();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [pendingId, setPendingId] = useState<string | null>(null);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [editing, setEditing] = useState<LiveMeditationSession | null>(null);

    // Derived, not state, so a background refetch never wipes the active search.
    const rows = useMemo(() => {
        const all = data?.data?.sessions ?? [];
        const query = search.trim().toLowerCase();
        if (!query) return all;
        return all.filter((s) => s.title.toLowerCase().includes(query) || (s.hostName || '').toLowerCase().includes(query));
    }, [data, search]);

    const mediaAvailable = data?.data?.mediaAvailable ?? false;
    const safePage = Math.min(page, Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1));
    const visible = rows.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);

    const handleRemove = async (session: LiveMeditationSession) => {
        const ok = window.confirm(`Remove "${session.title}"? Anyone currently in it will be dropped. This cannot be undone here.`);
        if (!ok) return;
        setPendingId(session.id);
        await removeSession(session.id);
        setPendingId(null);
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Live sessions</h3>
                    <h3 className="page-subhead subhead">
                        Our own in-app rooms. Includes sessions members created for their own groups — those are listed so they can be moderated, not
                        edited.
                    </h3>
                </Col>
            </Row>

            {/* Stated plainly rather than hidden: while the Cloudflare secrets are
                unprovisioned, a created session has no in-app room to open and
                falls back to the Google Meet link on its cohort. An admin
                discovering that at session time is the failure this avoids. */}
            {!isLoading && !mediaAvailable && (
                <Row>
                    <Col md={12}>
                        <div className="alert alert-warning" role="alert" style={{ marginBottom: 20 }}>
                            <strong>In-app video is not switched on yet.</strong> Sessions can be created, but until the Cloudflare credentials are
                            provisioned they cannot open an in-app room — a cohort session will use its Google Meet link instead.
                        </div>
                    </Col>
                </Row>
            )}

            <Row>
                <Col md={12}>
                    <Card>
                        <CardBody>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                                <div style={{ maxWidth: 320, flex: 1 }}>
                                    <MaterialTextField
                                        label="Search by session or host"
                                        value={search}
                                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                            setSearch(event.target.value);
                                            setPage(0);
                                        }}
                                    />
                                </div>
                                <button type="button" className="btn btn-primary" style={{ margin: 0 }} onClick={() => setIsCreateOpen(true)}>
                                    New session
                                </button>
                            </div>

                            {isLoading ? (
                                <LoadingSpinner />
                            ) : (
                                <>
                                    <TableWrap>
                                        <Table className="material-table">
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell>
                                                        <strong>Session</strong>
                                                    </TableCell>
                                                    <TableCell>
                                                        <strong>Host</strong>
                                                    </TableCell>
                                                    <TableCell>
                                                        <strong>Type</strong>
                                                    </TableCell>
                                                    <TableCell>
                                                        <strong>Participants may</strong>
                                                    </TableCell>
                                                    <TableCell>
                                                        <strong>Privacy</strong>
                                                    </TableCell>
                                                    <TableCell>
                                                        <strong>Status</strong>
                                                    </TableCell>
                                                    <TableCell>
                                                        <strong>When</strong>
                                                    </TableCell>
                                                    <TableCell>
                                                        <strong>Repeats</strong>
                                                    </TableCell>
                                                    <TableCell />
                                                </TableRow>

                                                {visible.map((session) => {
                                                    const status = deriveLiveSessionStatus(session);
                                                    return (
                                                        <TableRow key={session.id}>
                                                            <TableCell>
                                                                {session.title}
                                                                {session.description ? (
                                                                    <div style={{ opacity: 0.6, fontSize: 12 }}>{session.description}</div>
                                                                ) : null}
                                                            </TableCell>
                                                            <TableCell>{session.hostName}</TableCell>
                                                            <TableCell>
                                                                {MEDITATION_TYPE_LABELS[session.meditationType]} · {session.durationMinutes}m
                                                            </TableCell>
                                                            <TableCell style={{ fontSize: 12 }}>{describePolicies(session)}</TableCell>
                                                            <TableCell>{session.privacy}</TableCell>
                                                            <TableCell>
                                                                <span style={{ color: STATUS_COLOURS[status] || 'inherit', fontWeight: 600 }}>
                                                                    {status}
                                                                </span>
                                                            </TableCell>
                                                            <TableCell>{formatWhen(session)}</TableCell>
                                                            <TableCell style={{ fontSize: 12 }}>
                                                                {session.recurrence?.active
                                                                    ? session.recurrence.frequency === 'daily'
                                                                        ? `Daily · ${session.recurrence.timeOfDay}`
                                                                        : `Weekly · ${session.recurrence.timeOfDay}`
                                                                    : '—'}
                                                            </TableCell>
                                                            <TableCell>
                                                                <div style={{ display: 'flex', gap: 6 }}>
                                                                    {/* Edit is also how a FINISHED session is renewed: give it a
                                                                        new time and it comes back as a fresh occurrence. */}
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-primary btn-sm"
                                                                        style={{ margin: 0 }}
                                                                        onClick={() => setEditing(session)}
                                                                    >
                                                                        {status === 'ended' || status === 'cancelled' ? 'Renew' : 'Edit'}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-danger btn-sm"
                                                                        style={{ margin: 0 }}
                                                                        disabled={removing && pendingId === session.id}
                                                                        onClick={() => handleRemove(session)}
                                                                    >
                                                                        {removing && pendingId === session.id ? 'Removing…' : 'Remove'}
                                                                    </button>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}

                                                {visible.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={9} style={{ textAlign: 'center', opacity: 0.6 }}>
                                                            No sessions yet.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableWrap>

                                    <TablePagination
                                        component="div"
                                        className="material-table__pagination"
                                        count={rows.length}
                                        rowsPerPage={rowsPerPage}
                                        page={safePage}
                                        onPageChange={(_event: unknown, newPage: number) => setPage(newPage)}
                                        onRowsPerPageChange={(event: ChangeEvent<HTMLInputElement>) => {
                                            setRowsPerPage(Number(event.target.value));
                                            setPage(0);
                                        }}
                                        rowsPerPageOptions={[10, 25, 50]}
                                    />
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <CreateSessionModal isOpen={isCreateOpen} toggle={() => setIsCreateOpen((prev) => !prev)} />
            <CreateSessionModal isOpen={!!editing} session={editing} toggle={() => setEditing(null)} />
        </Container>
    );
};

export default LiveSessions;
